import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  ChevronDown,
  ChevronRight,
  Menu,
  X,
  LayoutDashboard,
  Warehouse,
  PackageOpen,
  ArrowLeftRight,
  Wrench,
  List,
  MapPin,
  LayoutGrid,
  Hammer,
  Tags,
  LayoutList,
  BarChart,
  CheckSquare,
  Boxes,
  ArrowDownToLine,
  ArrowUpFromLine,
  RefreshCw,
  Truck,
  Calendar,
  Laptop,
  FileCog,
  Scissors,
  LineChart,
  Target,
  Zap,
  LineChartIcon,
  Package
} from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

type MenuItem = {
  key: string;
  label: string;
  icon: React.ReactNode;
  to: string;
  children?: MenuItem[];
};

const createIcon = (iconName: string, size = 18) => {
  const icons: Record<string, React.ReactNode> = {
    "DashboardOutlined": <LayoutDashboard size={size} />,
    "UnorderedListOutlined": <List size={size} />,
    "EnvironmentOutlined": <MapPin size={size} />,
    "BorderOuterOutlined": <LayoutGrid size={size} />,
    "ToolOutlined": <Hammer size={size} />,
    "BoxPlotOutlined": <PackageOpen size={size} />,
    "TagsOutlined": <Tags size={size} />,
    "AppstoreOutlined": <LayoutList size={size} />,
    "BarChartOutlined": <BarChart size={size} />,
    "CheckSquareOutlined": <CheckSquare size={size} />,
    "BlockOutlined": <Boxes size={size} />,
    "SwapOutlined": <ArrowLeftRight size={size} />,
    "ImportOutlined": <ArrowDownToLine size={size} />,
    "ExportOutlined": <ArrowUpFromLine size={size} />,
    "RetweetOutlined": <RefreshCw size={size} />,
    "CarOutlined": <Truck size={size} />,
    "ScheduleOutlined": <Calendar size={size} />,
    "LaptopOutlined": <Laptop size={size} />,
    "SolutionOutlined": <FileCog size={size} />,
    "ScissorOutlined": <Scissors size={size} />,
    "FundProjectionScreenOutlined": <LineChart size={size} />,
    "AimOutlined": <Target size={size} />,
    "ThunderboltOutlined": <Zap size={size} />,
    "LineChartOutlined": <LineChartIcon size={size} />
  };

  return icons[iconName] || <div className="w-5 h-5" />;
};

// Define our menu items directly
const menuItems: MenuItem[] = [
  {
    key: "dashboard",
    label: "Dashboard",
    icon: <LayoutDashboard size={18} />,
    to: "/",
    children: undefined
  },
  {
    key: "warehouse",
    label: "Depo Yönetimi",
    icon: <Warehouse size={18} />,
    to: "/warehouse",
    children: [
      {
        key: "warehouse-list",
        label: "Depo Listesi",
        icon: <List size={18} />,
        to: "/warehouse/list"
      },
      {
        key: "warehouse-map",
        label: "Depo Haritası",
        icon: <MapPin size={18} />,
        to: "/warehouse/map"
      },
      {
        key: "warehouse-rack",
        label: "Raf Yönetimi",
        icon: <LayoutGrid size={18} />,
        to: "/warehouse/rack"
      },
      {
        key: "warehouse-equipment",
        label: "Ekipman Yönetimi",
        icon: <Hammer size={18} />,
        to: "/warehouse/equipment"
      }
    ]
  },
  {
    key: "inventory",
    label: "Stok & Envanter",
    icon: <PackageOpen size={18} />,
    to: "/inventory",
    children: [
      {
        key: "inventory-items",
        label: "Malzeme Yönetimi",
        icon: <Tags size={18} />,
        to: "/inventory/items"
      },
      {
        key: "inventory-categories",
        label: "Kategoriler",
        icon: <LayoutList size={18} />,
        to: "/inventory/categories"
      },
      {
        key: "inventory-stock",
        label: "Stok Durumu",
        icon: <BarChart size={18} />,
        to: "/inventory/stock"
      },
      {
        key: "inventory-counting",
        label: "Sayım İşlemleri",
        icon: <CheckSquare size={18} />,
        to: "/inventory/counting"
      },
      {
        key: "inventory-batch",
        label: "Parti Takibi",
        icon: <Boxes size={18} />,
        to: "/inventory/batch"
      }
    ]
  },
  {
    key: "logistics",
    label: "Lojistik İşlemler",
    icon: <ArrowLeftRight size={18} />,
    to: "/logistics",
    children: [
      {
        key: "logistics-inbound",
        label: "Depo Girişi",
        icon: <ArrowDownToLine size={18} />,
        to: "/logistics/inbound"
      },
      {
        key: "logistics-outbound",
        label: "Depo Çıkışı",
        icon: <ArrowUpFromLine size={18} />,
        to: "/logistics/outbound"
      },
      {
        key: "logistics-transfer",
        label: "Depolar Arası Transfer",
        icon: <RefreshCw size={18} />,
        to: "/logistics/transfer"
      },
      {
        key: "logistics-shipping",
        label: "Sevkiyat Yönetimi",
        icon: <Truck size={18} />,
        to: "/logistics/shipping"
      },
      {
        key: "logistics-loading",
        label: "Yükleme Planları",
        icon: <Calendar size={18} />,
        to: "/logistics/loading"
      }
    ]
  },
  {
    key: "operations",
    label: "Operasyonel Malzemeler",
    icon: <Wrench size={18} />,
    to: "/operations",
    children: [
      {
        key: "operations-equipment",
        label: "Ekipman Takibi",
        icon: <Laptop size={18} />,
        to: "/operations/equipment"
      },
      {
        key: "operations-assignment",
        label: "Zimmet İşlemleri",
        icon: <FileCog size={18} />,
        to: "/operations/assignment"
      },
      {
        key: "operations-consumables",
        label: "Sarf Malzeme Takibi",
        icon: <Scissors size={18} />,
        to: "/operations/consumables"
      }
    ]
  },
  {
    key: "planning",
    label: "Planlama & Tahmin",
    icon: <LineChart size={18} />,
    to: "/planning",
    children: [
      {
        key: "planning-needs",
        label: "İhtiyaç Planlama",
        icon: <Target size={18} />,
        to: "/planning/needs"
      },
      {
        key: "planning-disaster",
        label: "Afet Hazırlık",
        icon: <Zap size={18} />,
        to: "/planning/disaster"
      },
      {
        key: "planning-forecast",
        label: "Talep Tahmini",
        icon: <LineChartIcon size={18} />,
        to: "/planning/forecast"
      }
    ]
  }
];

const SubMenu = ({
  item,
  isOpen,
  toggleOpen,
  isActive,
  isMobile,
  closeSidebar
}: {
  item: MenuItem;
  isOpen: boolean;
  toggleOpen: () => void;
  isActive: boolean;
  isMobile: boolean;
  closeSidebar: () => void;
}) => {
  const location = useLocation();

  const hasActiveChild = item.children?.some(
    child => location.pathname === child.to ||
      location.pathname.startsWith(child.to + '/')
  );

  return (
    <div className={cn(
      "rounded-md overflow-hidden transition-colors duration-200",
      (isActive || hasActiveChild) && "bg-accent/50"
    )}>
      <button
        onClick={toggleOpen}
        className={cn(
          "w-full flex items-center justify-between px-3 py-2 text-sm font-medium rounded-md transition-colors",
          "hover:bg-accent",
          (isActive || hasActiveChild) && "text-primary font-semibold"
        )}
      >
        <div className="flex items-center gap-3">
          <div className="text-muted-foreground">{item.icon}</div>
          <span>{item.label}</span>
        </div>
        <div className="text-muted-foreground">
          {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </div>
      </button>

      <div
        className={cn(
          "pl-9 overflow-hidden transition-all duration-200 ease-in-out",
          isOpen ? "max-h-96" : "max-h-0"
        )}
      >
        {item.children?.map((child) => {
          const childIsActive = location.pathname === child.to ||
            location.pathname.startsWith(child.to + '/');

          return (
            <Link
              key={child.key}
              to={child.to}
              onClick={isMobile ? closeSidebar : undefined}
              className={cn(
                "flex items-center gap-3 px-3 py-2 text-sm rounded-md my-1 transition-colors",
                "hover:bg-accent",
                childIsActive
                  ? "text-primary font-medium bg-accent/80"
                  : "text-foreground/80"
              )}
            >
              <div className={cn(
                "text-muted-foreground",
                childIsActive && "text-primary"
              )}>
                {child.icon}
              </div>
              <span>{child.label}</span>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

const Sidebar = () => {
  const [openItems, setOpenItems] = useState<Record<string, boolean>>({});
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const isMobile = useIsMobile();

  // Close the sidebar when navigating on mobile
  useEffect(() => {
    if (isMobile) {
      setIsOpen(false);
    }
  }, [location.pathname, isMobile]);

  const toggleItem = (key: string) => {
    setOpenItems(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Auto-expand parent menu of active route on page load
  useEffect(() => {
    const newOpenItems: Record<string, boolean> = {};

    menuItems.forEach(item => {
      if (item.children) {
        const hasActiveChild = item.children.some(
          child => location.pathname === child.to ||
            location.pathname.startsWith(child.to + '/')
        );

        if (hasActiveChild) {
          newOpenItems[item.key] = true;
        }
      }
    });

    setOpenItems(prev => ({ ...prev, ...newOpenItems }));
  }, [location.pathname]);

  return (
    <>
      {/* Mobile menu overlay */}
      {isMobile && isOpen && (
        <div
          className="fixed inset-0 bg-background/80 backdrop-blur-sm z-40"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Mobile menu toggle */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed top-4 left-4 z-50 lg:hidden p-2 rounded-md bg-background border shadow-sm"
        aria-label="Toggle menu"
      >
        {isOpen ? <X size={20} /> : <Menu size={20} />}
      </button>

      {/* Sidebar */}
      <aside
        className={cn(
          "h-screen fixed top-0 left-0 z-40 flex flex-col border-r bg-sidebar transition-all duration-300",
          "w-64",
          isMobile && (isOpen ? "translate-x-0" : "-translate-x-full"),
          !isMobile && "translate-x-0"
        )}
      >
        <div className="p-4 border-b">
          <div className="flex items-center space-x-2">
            <Package className="h-6 w-6 text-primary" />
            <h1 className="font-semibold text-lg">Logistics</h1>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto py-2 px-2">
          {menuItems.map((item) => {
            const isActive =
              location.pathname === item.to ||
              location.pathname.startsWith(item.to + '/');

            if (item.children) {
              return (
                <SubMenu
                  key={item.key}
                  item={item}
                  isOpen={!!openItems[item.key]}
                  toggleOpen={() => toggleItem(item.key)}
                  isActive={isActive}
                  isMobile={isMobile}
                  closeSidebar={() => setIsOpen(false)}
                />
              );
            }

            return (
              <Link
                key={item.key}
                to={item.to}
                onClick={isMobile ? () => setIsOpen(false) : undefined}
                className={cn(
                  "flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-md mb-1 transition-colors",
                  "hover:bg-accent",
                  isActive
                    ? "bg-accent/80 text-primary"
                    : "text-foreground/80"
                )}
              >
                <div className={cn(
                  "text-muted-foreground",
                  isActive && "text-primary"
                )}>
                  {item.icon}
                </div>
                <span>{item.label}</span>
              </Link>
            );
          })}
        </div>

        <div className="p-4 border-t text-center text-sm text-muted-foreground">
          v1.0.0 • Logistics
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
